import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Bot, User, Sparkles } from 'lucide-react';
import { generateResponse } from '../../lib/groq';

type Message = {
  id: string;
  sender: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
};

const welcomeMessage = {
  id: 'welcome',
  sender: 'assistant' as const,
  content: "Hello! I'm your Renaissance AI assistant. How can I help you today? 🎓✨",
  timestamp: new Date(),
};

const suggestedQuestions = [
  "What programs do you offer?",
  "How do I apply for admission?",
  "What are your school hours?",
  "Tell me about your faculty"
];

import { ChatRole, ChatPart, ChatContent } from '../../lib/groq';

const ChatInterface = ({ onClose }: { onClose: () => void }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([welcomeMessage]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input when chat opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const userMessage = inputValue.trim();
    if (!userMessage) return;

    // Add user message to chat
    const userMessageObj: Message = {
      id: Date.now().toString(),
      sender: 'user',
      content: userMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessageObj]);
    setInputValue('');
    setIsLoading(true);

    // Add typing indicator
    const typingIndicator: Message = {
      id: 'typing',
      sender: 'assistant',
      content: '',
      timestamp: new Date(),
      isTyping: true
    };

    setMessages(prev => [...prev, typingIndicator]);

    try {
      // Prepare chat history for context
      const chatHistory: ChatContent[] = messages
        .filter(msg => msg.id !== 'welcome' && msg.id !== 'typing')
        .map(msg => ({
          role: (msg.sender === 'user' ? 'user' : 'assistant') as ChatRole,
          parts: [{ text: msg.content }]
        }));

      // Get response from backend (Groq)
      const response = await generateResponse(userMessage, chatHistory);
      
      // Remove typing indicator
      setMessages(prev => prev.filter(msg => msg.id !== 'typing'));
      
      // Add bot response
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        content: typeof response === 'string' ? response : 'I received an unexpected response format. Please try again.',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Error generating response:', error);
      // Remove typing indicator
      setMessages(prev => prev.filter(msg => msg.id !== 'typing'));
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        content: `I'm sorry, I encountered an error: ${error instanceof Error ? error.message : 'Please try again later.'}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle suggested question click
  const handleSuggestedQuestion = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 flex justify-between items-center shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="p-1.5 bg-white/20 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">Renaissance AI</h3>
            <p className="text-xs text-white/90">How can I help you today?</p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close chat"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              {message.isTyping ? (
                <div className="flex space-x-1.5 p-3 bg-white rounded-2xl shadow-sm">
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              ) : (
                <div 
                  className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 ${
                    message.sender === 'user' 
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-br-none' 
                      : 'bg-white border border-gray-100 shadow-sm rounded-bl-none text-gray-800'
                  }`}
                >
                  <div className="whitespace-pre-line text-sm md:text-base">{message.content}</div>
                  <div className={`text-xs mt-1.5 opacity-80 text-right ${
                    message.sender === 'user' ? 'text-orange-100' : 'text-gray-500'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={messagesEndRef} />

        {/* Suggested Questions */}
        {messages.length <= 1 && (
          <motion.div 
            className="mt-6 space-y-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-xs font-medium text-gray-500 text-center">Try asking:</p>
            <div className="grid grid-cols-2 gap-2">
              {suggestedQuestions.map((question, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleSuggestedQuestion(question)}
                  className="text-xs text-left p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {question}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Input Area */}
      <form 
        onSubmit={handleSendMessage} 
        className="p-4 border-t border-gray-100 bg-white"
      >
        <div className="relative flex items-center">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message..."
            className="w-full px-4 py-3 pr-12 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-transparent"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="absolute right-2 p-2 text-orange-500 hover:text-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;
